"use client";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Alert, Spin, Typography } from "antd";
import { useTranslation } from "react-i18next";

import AppLayout from "../../../navigation/Layout/Layout";
import ScrollTabs from "../../../Profile/components/ScrollTabs/ScrollTabs";
import { ROUTES } from "../../../routes/patientRoutes/routePaths";
import useAuthSession from "../../../authPatient/hooks/useAuthSession";
import useExtraction from "../../hooks/useExtraction";
import { useTour } from "../../../appointments/hooks/useTour";
import { medicalRecordTourSteps } from "./medicalRecordTourConfig";

import MedicalHistory from "../../components//MedicalHistory/MedicalHistory";
import MedicalRecordUpload from "../../components//MedicalRecordUpload/MedicalRecordUpload";
import RecentRecords from "../../components//RecentRecords/RecentRecords";

import "./MedicalRecord.css";

const { Text } = Typography;

function getActiveKey(pathname, userId) {
  const p = (pathname || "").toLowerCase();
  if (p.startsWith(ROUTES.OVERVIEWS.toLowerCase())) return ROUTES.OVERVIEWS;
  if (p.startsWith(ROUTES.MEDICAL_RECORD.toLowerCase())) return ROUTES.MEDICAL_RECORD;
  if (p.startsWith("/profile")) return ROUTES.PROFILE(userId || ":userId");
  return ROUTES.MEDICAL_RECORD;
}

export default function MedicalRecordPage() {
  const { t, i18n } = useTranslation();

  const {
    auth,
    loading,
    effectiveUserId,
    effectiveUser,
    principalUserId,
    activeSubprofileId,
  } = useAuthSession({ auto: true });
  const currentUser = effectiveUser || auth?.user;
  const userId = effectiveUserId || null;
  const ownerUserId = activeSubprofileId ? principalUserId : userId;
  const scopeSubprofileId = activeSubprofileId || null;

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [shouldStartTour, setShouldStartTour] = useState(false);

  const activeKey = getActiveKey(pathname, userId);
  const onTabChange = (key) => navigate(key);

  const {
    myDocuments,
    myDocumentsLoading,
    statistics,
    isHealthy,
    loadMyDocuments,
    refreshAllData,
  } = useExtraction(ownerUserId, { subprofileId: scopeSubprofileId });

  useEffect(() => {
    if (userId) {
      loadMyDocuments(0, 50);
    }
  }, [userId, loadMyDocuments]);

  useEffect(() => {
    const hasGuide = searchParams.get("guide") === "true";
    const hasTour = searchParams.get("tour") === "medical_record";

    if (hasGuide || hasTour) {
      setTimeout(() => {
        const mrContent = document.querySelector(".mr-content");
        const mrGrid = document.querySelector(".mr-grid");
        const mrColLeft = document.querySelector(".mr-col--left");

        if (mrContent && mrGrid && mrColLeft) {
          setShouldStartTour(true);
        } else {
          setTimeout(() => setShouldStartTour(true), 1500);
        }
      }, 1000);
    }
  }, [searchParams]);

  useTour(shouldStartTour, medicalRecordTourSteps);

  const recentRecords = useMemo(() => {
    if (!myDocuments || myDocuments.length === 0) {
      return [];
    }

    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

    return myDocuments
      .filter((doc) => {
        const docDate = new Date(doc.date_upload || doc.createdAt || Date.now());
        return docDate >= threeMonthsAgo;
      })
      .slice(0, 5)
      .map((doc) => {
        const docDate = new Date(doc.date_upload || doc.createdAt || Date.now());

        const formattedDate = docDate.toLocaleDateString(i18n.language || "en", {
          day: "2-digit",
          month: "long",
        });

        const docType = (doc.type_document || "AUTRE").toUpperCase();

        return {
          id: doc._id || doc.id,
          dateLabel: formattedDate,
          title:
            doc.nom_fichier ||
            doc.type_document ||
            t("medicalRecordd.defaults.documentTitle", "Medical Document"),
          type: doc.type_document,
          pdfUrl: doc.chemin_fichier || doc.file_path || doc.pdfUrl || doc.url,
          fileUrl: doc.chemin_fichier || doc.file_path || doc.pdfUrl || doc.url,
          url: doc.chemin_fichier || doc.file_path || doc.pdfUrl || doc.url,
          patientId: userId, // Injection explicite de l'id patient pour RecentRecords/ShareModal
          // ✅ FIX: onClick is now only called when NOT in selection mode (handled in RecordItem)
          // The navigation happens only when the user explicitly clicks to open the document,
          // not when they're selecting it for sharing.
          onClick: () => {
            navigate(`/records/${docType}`);
          },
        };
      });
  }, [myDocuments, navigate, t, i18n.language, userId]);

  // ✅ FIX: handler for when a record is selected (not opened)
  // This is called by RecentRecords when selectionMode is OFF and user wants to view/open
  // We keep it undefined here so RecordItem falls back to calling onClick (navigate)
  // If you need a custom select behavior, implement it here.
  const handleRecordSelect = undefined;

  if (loading && !currentUser) {
    return (
      <AppLayout>
        <div className="mr-page">
          <ScrollTabs mode="sticky" activeKey={activeKey} onChange={onTabChange} userId={userId} />
          <div className="mr-center">
            <Spin size="large" />
            <Text type="secondary" style={{ marginTop: 8, display: "block" }}>
              {t("common.loading", "Loading...")}
            </Text>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!userId) {
    return (
      <AppLayout>
        <div className="mr-page">
          <ScrollTabs mode="sticky" activeKey={activeKey} onChange={onTabChange} userId={userId} />
          <div className="mr-center">
            <Alert
              message={t("medicalRecordd.auth.unauthorizedTitle", "Unauthorized Access")}
              description={t(
                "medicalRecordd.auth.unauthorizedDesc",
                "Please log in to access your medical records."
              )}
              type="warning"
              showIcon
            />
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="mr-page">
        <ScrollTabs activeKey={activeKey} onChange={onTabChange} userId={userId} />

        <div className="mr-content">
          <div className="mr-grid">
            <div className="mr-col mr-col--left">
              {!isHealthy && (
                <Alert
                  className="mr-alert-s"
                  message={t(
                    "medicalRecordd.serviceUnavailable.title",
                    "Extraction Service Temporarily Unavailable"
                  )}
                  description={t(
                    "medicalRecordd.serviceUnavailable.desc",
                    "The medical document analysis service is currently unavailable. Please try again later."
                  )}
                  type="warning"
                  showIcon
                  closable
                  style={{ marginBottom: 16 }}
                />
              )}

              <div className="mr-card mr-card--upload">
                <MedicalRecordUpload
                  userId={ownerUserId}
                  subprofileId={scopeSubprofileId}
                />
              </div>

              <div className="mr-card mr-card--recent">
                <RecentRecords
                  records={recentRecords}
                  loading={myDocumentsLoading}
                  onFindLab={() => navigate("/labs")}
                  // ✅ FIX: onSelect is intentionally not passed here — navigation
                  // is handled by the onClick on each record item instead.
                  // This prevents accidental navigation during selection mode.
                  onSelect={handleRecordSelect}
                  onShare={() => {}}
                  onShareSuccess={() => {}}
                />
              </div>
            </div>

            <div className="mr-col mr-col--right">
              <div className="mr-card mr-card--history">
                <MedicalHistory
                  userId={ownerUserId}
                  subprofileId={scopeSubprofileId}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
